'use client';

import { useScroll, useTransform, motion } from 'framer-motion';
import { useRef } from 'react';

interface Image {
  src: string;
  alt?: string;
  srcSetAvif?: string;
  srcSetWebp?: string;
  sizes?: string;
}

interface ZoomParallaxProps {
  images: Image[];
}

export function ZoomParallax({ images }: ZoomParallaxProps) {
  const container = useRef<HTMLDivElement | null>(null);
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ['start start', 'end end'],
  });

  const scale4 = useTransform(scrollYProgress, [0, 1], [1, 4]);
  const scale5 = useTransform(scrollYProgress, [0, 1], [1, 5]);
  const scale6 = useTransform(scrollYProgress, [0, 1], [1, 6]);
  const scale8 = useTransform(scrollYProgress, [0, 1], [1, 8]);
  const scale9 = useTransform(scrollYProgress, [0, 1], [1, 9]);

  const scales = [scale4, scale5, scale6, scale5, scale6, scale8, scale9];

  return (
    <div ref={container} className="relative h-[300vh]">
      <div className="sticky top-0 h-screen overflow-hidden">
        {images.map(({ src, alt }, index) => {
          const scale = scales[index % scales.length];
          return (
            <motion.div
              key={index}
              style={{ scale }}
              className={`absolute top-0 flex h-full w-full items-center justify-center ${index === 1 ? '[&>div]:!-top-[30vh] [&>div]:!left-[5vw] [&>div]:!h-[30vh] [&>div]:!w-[35vw]' : ''} ${index === 2 ? '[&>div]:!-top-[10vh] [&>div]:!-left-[25vw] [&>div]:!h-[45vh] [&>div]:!w-[20vw]' : ''} ${index === 3 ? '[&>div]:!left-[27.5vw] [&>div]:!h-[25vh] [&>div]:!w-[25vw]' : ''} ${index === 4 ? '[&>div]:!top-[27.5vh] [&>div]:!left-[5vw] [&>div]:!h-[25vh] [&>div]:!w-[20vw]' : ''} ${index === 5 ? '[&>div]:!top-[27.5vh] [&>div]:!-left-[22.5vw] [&>div]:!h-[25vh] [&>div]:!w-[30vw]' : ''} ${index === 6 ? '[&>div]:!top-[22.5vh] [&>div]:!left-[25vw] [&>div]:!h-[15vh] [&>div]:!w-[15vw]' : ''} ${index === 7 ? '[&>div]:!top-[20vh] [&>div]:!-left-[12vw] [&>div]:!h-[28vh] [&>div]:!w-[22vw]' : ''} ${index === 8 ? '[&>div]:!top-[8vh] [&>div]:!left-[82vw] [&>div]:!h-[26vh] [&>div]:!w-[22vw]' : ''} ${index === 9 ? '[&>div]:!-top-[15vh] [&>div]:!left-[55vw] [&>div]:!h-[32vh] [&>div]:!w-[24vw]' : ''} ${index === 10 ? '[&>div]:!top-[35vh] [&>div]:!left-[60vw] [&>div]:!h-[20vh] [&>div]:!w-[18vw]' : ''} ${index === 11 ? '[&>div]:!-top-[5vh] [&>div]:!left-[10vw] [&>div]:!h-[18vh] [&>div]:!w-[18vw]' : ''} ${index === 12 ? '[&>div]:!top-[40vh] [&>div]:!-left-[5vw] [&>div]:!h-[22vh] [&>div]:!w-[20vw]' : ''} ${index === 13 ? '[&>div]:!top-[15vh] [&>div]:!left-[72vw] [&>div]:!h-[22vh] [&>div]:!w-[18vw]' : ''} ${index === 14 ? '[&>div]:!top-[45vh] [&>div]:!left-[78vw] [&>div]:!h-[18vh] [&>div]:!w-[16vw]' : ''} `}
            >
              <div className="relative h-[25vh] w-[25vw] rounded-2xl overflow-hidden border border-border bg-card/20">
                {(images[index].srcSetAvif || images[index].srcSetWebp) ? (
                  <picture>
                    {images[index].srcSetAvif ? (
                      <source type="image/avif" srcSet={images[index].srcSetAvif} sizes={images[index].sizes || '25vw'} />
                    ) : null}
                    {images[index].srcSetWebp ? (
                      <source type="image/webp" srcSet={images[index].srcSetWebp} sizes={images[index].sizes || '25vw'} />
                    ) : null}
                    <img
                      src={src || '/placeholder.svg'}
                      alt={alt || `Parallax image ${index + 1}`}
                      loading="lazy"
                      decoding="async"
                      fetchPriority="low"
                      className="h-full w-full object-cover rounded-2xl"
                    />
                  </picture>
                ) : (
                  <img
                    src={src || '/placeholder.svg'}
                    alt={alt || `Parallax image ${index + 1}`}
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                    className="h-full w-full object-cover rounded-2xl"
                  />
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}


